// Prompt files: ten files, 20 MiB each, of any type (Poulpe-478).
// Bytes are uploaded to the server; runner jobs carry references.
import { useEffect, useState, type SVGProps } from 'react'
import type { WireAttachment } from '../api/types'
import { bytesText, FileIcon } from '../transcript/SentFiles'

/** The most files one prompt takes. */
export const PROMPT_MOST = 10
/** The largest file a prompt takes. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024
/** The most bytes the files of a prompt hold together. */
export const MAX_TOTAL_BYTES = PROMPT_MOST * MAX_FILE_BYTES

export const NO_FILES: readonly File[] = []

/** The files a drop holds, without the folders among them: a folder has no bytes to send. */
export function droppedFiles(data: DataTransfer): { files: File[]; folders: number } {
  const files: File[] = []
  let folders = 0
  const items = data.items ? Array.from(data.items) : []
  if (items.length === 0) return { files: Array.from(data.files ?? []), folders }
  for (const item of items) {
    if (item.kind !== 'file') continue
    if (item.webkitGetAsEntry?.()?.isDirectory) {
      folders += 1
      continue
    }
    const file = item.getAsFile()
    if (file) files.push(file)
  }
  return { files, folders }
}

/**
 * The files a paste holds that are his to attach: an image he copied, a
 * file he copied in the Finder. Text copied from a document often comes
 * with a picture of itself: what holds both plain and rich text is pasted
 * as the text it is.
 */
export function pastedFiles(data: DataTransfer): File[] {
  const files = Array.from(data.files ?? [])
  const types = Array.from(data.types ?? [])
  if (files.length === 0 || (types.includes('text/plain') && types.includes('text/html'))) return []
  return files
}

/** What is attached once `found` joins `held`, and what to tell him of those that do not. */
export function withFiles(held: readonly File[], found: readonly File[], folders = 0): { files: readonly File[]; refused: string[] } {
  const refused: string[] = []
  if (folders > 0) refused.push(folders === 1 ? 'A folder cannot be attached: attach its files.' : `${folders} folders cannot be attached: attach their files.`)
  let total = held.reduce((sum, file) => sum + file.size, 0)
  const taken: File[] = []
  let over = 0
  let together = false
  for (const file of found) {
    if (file.size === 0) refused.push(`“${file.name}” holds nothing: it was not attached.`)
    else if (file.size > MAX_FILE_BYTES) refused.push(`“${file.name}” is larger than 20 MB: it was not attached.`)
    else if (held.length + taken.length >= PROMPT_MOST) over += 1
    else if (total + file.size > MAX_TOTAL_BYTES) together = true
    else {
      taken.push(file)
      total += file.size
    }
  }
  if (over > 0) refused.push(`A prompt takes ${PROMPT_MOST} files: ${over === 1 ? 'one was' : `${over} were`} not attached.`)
  if (together) refused.push('The files of a prompt are at most 200 MB together: some were not attached.')
  return { files: taken.length > 0 ? [...held, ...taken] : held, refused }
}

/** A file's bytes as base64. */
async function base64Of(file: File): Promise<string> {
  const bytes = new Uint8Array(await readBytes(file))
  let binary = ''
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000))
  return btoa(binary)
}

function readBytes(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'))
    reader.readAsArrayBuffer(file)
  })
}

/** The files as `prompt_session` takes them. A file that cannot be read any more (moved or deleted since it was attached) throws, saying which. */
export async function wireFiles(files: readonly File[]): Promise<WireAttachment[]> {
  const wire: WireAttachment[] = []
  for (const file of files) {
    try {
      wire.push({ name: file.name, mime_type: file.type, data: await base64Of(file) })
    } catch {
      throw new Error(`“${file.name}” could not be read: attach it again.`)
    }
  }
  return wire
}

/** What identifies the files of one send, so that the same send again goes with the same mutation id. */
export const filesKey = (files: readonly File[]): unknown[] => files.map((file) => [file.name, file.size, file.lastModified, file.type])

/** The paperclip of the button that picks files. */
export function ClipIcon({ size = 14, ...rest }: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" {...rest}>
      <path d="M12.5 7.25 8 11.75a2.5 2.5 0 0 1-3.5-3.5l4.75-4.75a1.6 1.6 0 0 1 2.25 2.25L6.9 10.4a.7.7 0 0 1-1-1L10 5.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Thumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return
    let made: string
    try {
      made = URL.createObjectURL(file)
    } catch {
      return // an image that cannot be shown small is a file like any other
    }
    setUrl(made)
    return () => {
      setUrl(null)
      URL.revokeObjectURL(made)
    }
  }, [file])
  return url ? <img className="code-attachment-thumb" src={url} alt="" /> : <FileIcon />
}

/** The files that wait with the prompt he writes, each removable. Nothing while none waits. */
export function AttachedFiles({ files, onRemove }: { files: readonly File[]; onRemove: (index: number) => void }) {
  if (files.length === 0) return null
  return (
    <ul className="code-attachments" aria-label="Files attached to the prompt">
      {files.map((file, index) => (
        // The same file may be attached twice: its place tells the two apart.
        <li key={`${index}:${file.name}:${file.size}`} className="code-attachment">
          <Thumb file={file} />
          <span className="code-attachment-name" title={file.name}>
            {file.name}
          </span>
          <span className="code-attachment-size">{bytesText(file.size)}</span>
          <button type="button" className="code-attachment-remove" aria-label={`Remove ${file.name}`} title="Remove" onClick={() => onRemove(index)}>
            <span aria-hidden="true">×</span>
          </button>
        </li>
      ))}
    </ul>
  )
}
